import React, { ReactElement, useEffect, useRef, useState } from "react";
import { Trans, useTranslation } from "react-i18next";
import { useQuery } from "@apollo/client";

import radioIcon from "../../../assets/images/icons/check.svg";
import {
  I18N_NAMESPACES,
  TASTEBUD_PROFILE_STATUS_FILTER,
  WINE_OAK_LEVEL,
  DB_ID_WINE_CHARACTERISTIC_INFO_ACIDITY,
  DB_ID_WINE_CHARACTERISTIC_INFO_SWEETNESS,
  DB_ID_WINE_CHARACTERISTIC_INFO_TANNINS,
  DB_ID_WINE_CHARACTERISTIC_INFO_BODY,
  DB_ID_WINE_CHARACTERISTIC_INFO_FRUITINESS,
  DB_ID_WINE_CHARACTERISTIC_INFO_OAK,
  DB_ID_WINE_CHARACTERISTIC_INFO_ALCOHOL,
  MIN_RATED_WINES_FOR_TASTE_PROFILE,
  FETCH_POLICY_CACHE_AND_NETWORK,
} from "../../../helpers/constants";
import Title from "../../atoms/Title";
import StyledLoading from "../../atoms/Loading/StyledLoading";
import { GET_MEMBER_RATING_TASTE_PROFILE_BY_WINE_CLASS } from "../../../graphql/resolvers/member";
import { GetWine_wine } from "../../../types/__generated__/GetWine";
import { GET_WINE_TASTING_RANGES } from "../../../graphql/resolvers/wine";
import { WineTastingRanges } from "../../../types/__generated__/WineTastingRanges";
import { MemberRatingTasteProfileByWineClass } from "../../../types/__generated__/MemberRatingTasteProfileByWineClass";
import ButtonLink from "../../atoms/ButtonLink";
import { isLoggedIn } from "../../../helpers/auth";
import URL_PATTERNS from "../../../urls";
import { colors } from "../../../styles/variables";
import { useDeviceSize } from "../../../helpers/hooks";
import {
  TastebudProfileContainer,
  EmptyStateContainer,
  UmbrellaImage,
  TastebudContainer,
  RowContainer,
  TastebudLabelContainer,
  RadioIconsContainer,
  FloatingLevelsBar,
  TextLabelContainer,
  TextLabel,
  RadioIconContainer,
  RadioIcon,
  VerticalWhiteLine,
  TasteProfileTitle,
  TastingNotes,
  TasteProfileButton,
  ButtonRow,
  UnlockTitle,
  EmptyStateText,
  Gradient,
  StyledLink,
} from "./style";
import {
  alcoholPercentageQueryLabels,
  normalizeWineCharacteristicValue,
  radioIconMiddleValue,
  radioIconWidth,
} from "./helper";
import { getAlcoholBandPlotPosition } from "./alcoholBands";

interface TastebudProfileListPlotV2Props {
  wine: GetWine_wine | null | undefined;
  isWineDetailsPage: boolean;
  isOrangeWine: boolean;
}

interface TastebudsObjectProps {
  title: string | null | undefined;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  queryLabels: any;
  ratingData: number | null | undefined;
  wineData: number | null | undefined;
  id: number;
}

const TastebudProfileListPlotV2 = ({
  wine,
  isWineDetailsPage,
  isOrangeWine,
}: TastebudProfileListPlotV2Props) => {
  const floatingBarRef = useRef<HTMLDivElement | null>(null);

  const wineClassId = `${wine?.wineType?.wineClass?.id}`;

  const { isMedium } = useDeviceSize();
  const { t } = useTranslation([I18N_NAMESPACES.MY_TASTE_PROFILE, I18N_NAMESPACES.COMMON]);
  const [selectedStatus, setSelectedStatus] = useState<string>(TASTEBUD_PROFILE_STATUS_FILTER.THIS_WINE);

  const { loading: isMemberRatingTasteProfileLoading, data: memberRatingTasteProfile } =
    useQuery<MemberRatingTasteProfileByWineClass>(GET_MEMBER_RATING_TASTE_PROFILE_BY_WINE_CLASS, {
      skip: !isLoggedIn() || !wine?.wineType?.wineClass?.id,
      variables: { wineClassId: wine?.wineType?.wineClass?.id },
      partialRefetch: true,
      fetchPolicy: FETCH_POLICY_CACHE_AND_NETWORK,
    });

  const { loading: isWineTastingRangesLoading, data: wineTastingRanges } = useQuery<WineTastingRanges>(
    GET_WINE_TASTING_RANGES,
    {
      partialRefetch: true,
    }
  );

  const isLoading = isMemberRatingTasteProfileLoading || isWineTastingRangesLoading;

  /**
   * Returns the width of floating bar for various screen widths.
   * */
  const getFloatingPointBarWidth = (): number => {
    const { current } = floatingBarRef || {};
    const fallbackValue = isMedium ? 375 : 360;
    return current?.offsetWidth ?? fallbackValue;
  };

  const [floatingBarWidthHelper, setFloatingBarWidthHelper] = useState(getFloatingPointBarWidth());

  /**
   * Resets the state of page to display the correct floating bar width when users resize their window.
   * */
  const handleResize = () => {
    const newFloatingBarWidth = getFloatingPointBarWidth();
    if (newFloatingBarWidth !== floatingBarWidthHelper) {
      setFloatingBarWidthHelper(newFloatingBarWidth);
    }
  };

  useEffect(() => {
    // Add event listener and set initial window size when component mounts.
    window.addEventListener("resize", handleResize);
    handleResize();

    // Remove event listener when component un-mounts.
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const { memberRatingTastebudProfileByWineClass } = memberRatingTasteProfile || {};
  const { ratingTastebudProfile, numberOfRatings } = memberRatingTastebudProfileByWineClass || {};

  const {
    tanninAverage,
    bodyAverage,
    oakyAverage,
    acidityAverage,
    sweetnessAverage,
    fruitDrivenAverage,
    alcoholPercentageAverage,
  } = ratingTastebudProfile || {};

  const {
    allWineSweetnesses,
    allWineTannins,
    allWineBodies,
    allWineFruitDrivens,
    allWineAcidities,
    allWineOakies,
  } = wineTastingRanges || {};

  /**
   * Parses the wineData so that it can be consumed by the radio icon positioning functions.
   * */
  const getParsedWineData = () => {
    const wineOakLevel = wine?.wineOaky?.level;
    let wineOakScore;
    switch (wineOakLevel) {
      case WINE_OAK_LEVEL.NONE:
        wineOakScore = 0;
        break;

      case WINE_OAK_LEVEL.LOTS:
        wineOakScore = 1;
        break;

      default:
        wineOakScore = 0.5;
    }

    const alcoholPercentage = getAlcoholBandPlotPosition(wine?.alcoholPercentage);

    return {
      sweetness: normalizeWineCharacteristicValue(wine?.wineSweetness?.level),
      oakies: wineOakScore,
      bodyAverage: normalizeWineCharacteristicValue(wine?.wineBody?.level),
      fruitDrivenAverage: normalizeWineCharacteristicValue(wine?.wineFruitDriven?.level),
      tanninAverage: normalizeWineCharacteristicValue(wine?.wineTannin?.level),
      alcoholPercentage,
      acidityAverage: normalizeWineCharacteristicValue(wine?.wineAcidity?.level),
    };
  };

  const parsedWineData = getParsedWineData();

  // Structured data to render each taste bud characteristics.
  const tastebudsArray = [
    {
      title: t("myTasteProfile:tastebudProfile.categories.sweet"),
      queryLabels: allWineSweetnesses,
      ratingData: sweetnessAverage,
      wineData: parsedWineData?.sweetness,
      id: DB_ID_WINE_CHARACTERISTIC_INFO_SWEETNESS,
    },
    {
      title: t("myTasteProfile:tastebudProfile.categories.body"),
      queryLabels: allWineBodies,
      ratingData: bodyAverage,
      wineData: parsedWineData?.bodyAverage,
      id: DB_ID_WINE_CHARACTERISTIC_INFO_BODY,
    },
    {
      title: t("myTasteProfile:tastebudProfile.categories.fruity"),
      queryLabels: allWineFruitDrivens,
      ratingData: fruitDrivenAverage,
      wineData: parsedWineData?.fruitDrivenAverage,
      id: DB_ID_WINE_CHARACTERISTIC_INFO_FRUITINESS,
    },
    {
      title: t("myTasteProfile:tastebudProfile.categories.tannin"),
      queryLabels: allWineTannins,
      ratingData: tanninAverage,
      wineData: parsedWineData?.tanninAverage,
      id: DB_ID_WINE_CHARACTERISTIC_INFO_TANNINS,
    },
    {
      title: t("myTasteProfile:tastebudProfile.categories.acidity"),
      queryLabels: allWineAcidities,
      ratingData: acidityAverage,
      wineData: parsedWineData?.acidityAverage,
      id: DB_ID_WINE_CHARACTERISTIC_INFO_ACIDITY,
    },
    {
      title: t("myTasteProfile:tastebudProfile.categories.oak"),
      queryLabels: allWineOakies,
      ratingData: oakyAverage,
      wineData: parsedWineData?.oakies,
      id: DB_ID_WINE_CHARACTERISTIC_INFO_OAK,
    },
    {
      title: t("myTasteProfile:tastebudProfile.categories.alcohol"),
      queryLabels: alcoholPercentageQueryLabels,
      // Unlike the other rating averages, the API returns this as a raw ABV.
      ratingData: getAlcoholBandPlotPosition(alcoholPercentageAverage),
      wineData: parsedWineData?.alcoholPercentage,
      id: DB_ID_WINE_CHARACTERISTIC_INFO_ALCOHOL,
    },
  ];

  const radioIconHalfWidth = radioIconWidth / 2;

  const isWineFilterOn = selectedStatus === TASTEBUD_PROFILE_STATUS_FILTER.THIS_WINE;
  const isRatingsFilterOn = selectedStatus === TASTEBUD_PROFILE_STATUS_FILTER.MEMBER_RATINGS;

  const shouldShowEmptyState = !ratingTastebudProfile && isRatingsFilterOn;

  /**
   * Returns the absolute value positions of a radio icon.
   * */
  const getPositionOfRadioIcon = (ratingScore: number): number => {
    const floatingBarWidth = getFloatingPointBarWidth();
    const maxLeftPosition = floatingBarWidth - radioIconWidth;
    let preciseLocation = floatingBarWidth * ratingScore - radioIconHalfWidth; // horizontal location.
    preciseLocation = Math.max(preciseLocation, 0); // Prevents icon going to left of bar.
    preciseLocation = Math.min(preciseLocation, maxLeftPosition); // Prevents icon going to right of bar.

    return preciseLocation;
  };

  /**
   * Renders the group of radio icons.
   * @return {Component}
   * */
  const renderRadioIcons = (tastebudObject: TastebudsObjectProps): ReactElement => {
    const { queryLabels, ratingData, wineData } = tastebudObject || {};

    const wineRadioPosition = getPositionOfRadioIcon(wineData ?? radioIconMiddleValue);
    const ratingRadioPosition = getPositionOfRadioIcon(ratingData ?? radioIconMiddleValue);
    const isWineRadioLeftOfRatingRadio = !!(wineRadioPosition < ratingRadioPosition);
    const floatingBarWidth = getFloatingPointBarWidth();
    return (
      <RadioIconsContainer width={floatingBarWidth}>
        <FloatingLevelsBar ref={floatingBarRef}>
          <VerticalWhiteLine />
          <RadioIconContainer leftPosition={wineRadioPosition} radioIconWidth={radioIconWidth}>
            <RadioIcon src={radioIcon} />
          </RadioIconContainer>
          {isRatingsFilterOn && (
            <>
              <RadioIconContainer
                leftPosition={ratingRadioPosition}
                bgColor={colors.blue}
                radioIconWidth={radioIconWidth}
                isRatingIcon
              >
                <RadioIcon src={radioIcon} />
              </RadioIconContainer>
              <Gradient
                isWineRadioLeftOfRatingRadio={isWineRadioLeftOfRatingRadio}
                ratingRadioPosition={ratingRadioPosition}
                wineRadioPosition={wineRadioPosition}
              />
            </>
          )}
        </FloatingLevelsBar>
        <TextLabelContainer>
          {queryLabels?.map((item: { shortName: string; id: number }) => {
            const { id, shortName } = item || { shortName: "" };
            return <TextLabel key={id}>{shortName}</TextLabel>;
          })}
        </TextLabelContainer>
      </RadioIconsContainer>
    );
  };

  /**
   * Renders each taste bud characteristics such as,
   * sweetness, oak, fruitiness, body, acidity, tannins.
   * */
  const renderTastebudObject = (tastebudObject: TastebudsObjectProps): ReactElement => {
    const { title } = tastebudObject || {};
    return (
      <TastebudContainer key={title}>
        <RowContainer>
          <TastebudLabelContainer>
            <Title tag={6} uppercase tightLineHeight mb0>
              {title || ""}
            </Title>
          </TastebudLabelContainer>
          {renderRadioIcons(tastebudObject)}
        </RowContainer>
      </TastebudContainer>
    );
  };

  const renderTastingNotes = (): ReactElement => (
    <TastingNotes>
      {isWineFilterOn ? (
        t("myTasteProfile:thisWineTastingNotes")
      ) : (
        <>
          <Trans
            i18nKey="myTasteProfile:palateBasedOn"
            values={{
              wineType: wine?.wineType?.wineClass?.name?.toLowerCase(),
              amount: numberOfRatings ?? 0,
            }}
            components={{
              1: (
                <StyledLink
                  to={{
                    pathname: URL_PATTERNS.MY_CELLAR_FILTER_BY_CLASS(wineClassId),
                  }}
                  target="_blank"
                />
              ),
              bold: <strong />,
            }}
          />
          {isOrangeWine && ` ${t("myTasteProfile:orangeWineFromWhiteGrapes")}`}
        </>
      )}
    </TastingNotes>
  );

  const renderAllTastebudObjects = (): ReactElement[] =>
    tastebudsArray?.map((tastebudObject: TastebudsObjectProps) => renderTastebudObject(tastebudObject));

  const renderEmptyState = (): ReactElement =>
    isLoggedIn() ? (
      <EmptyStateContainer>
        <UmbrellaImage alt="gpd umbrella" />
        <UnlockTitle>{t("myTasteProfile:unlockPalateProfile")}</UnlockTitle>
        <EmptyStateText centerAlignText>
          {t("myTasteProfile:headsUp", {
            wineType: wine?.wineType?.wineClass?.name.toLowerCase(),
            amount: MIN_RATED_WINES_FOR_TASTE_PROFILE - (numberOfRatings ?? 0),
          })}
        </EmptyStateText>
        <ButtonLink full to={URL_PATTERNS.MY_CELLAR} target="_blank">
          {t("myTasteProfile:lockedScreen.topButtonCopy")}
        </ButtonLink>
      </EmptyStateContainer>
    ) : (
      <>
        <EmptyStateText>{t("myTasteProfile:createAnAccount")}</EmptyStateText>
        <ButtonLink full to={URL_PATTERNS.QUIZ}>
          {t("common:takeQuiz")}
        </ButtonLink>
      </>
    );

  const renderContent = (): ReactElement => (
    <TastebudProfileContainer isWineDetailsPage={isWineDetailsPage} data-testid="wine-taste-profile">
      <TasteProfileTitle>{t("myTasteProfile:tasteProfile")}</TasteProfileTitle>
      <ButtonRow>
        <TasteProfileButton
          color={colors.coral}
          isSelected={isWineFilterOn}
          onClick={() => setSelectedStatus(TASTEBUD_PROFILE_STATUS_FILTER.THIS_WINE)}
        >
          {t("myTasteProfile:thisWine")}
        </TasteProfileButton>
        <TasteProfileButton
          color={colors.blueTint}
          isSelected={isRatingsFilterOn}
          onClick={() => setSelectedStatus(TASTEBUD_PROFILE_STATUS_FILTER.MEMBER_RATINGS)}
        >
          {t("myTasteProfile:compareToMyPalate")}
        </TasteProfileButton>
      </ButtonRow>
      {shouldShowEmptyState ? (
        renderEmptyState()
      ) : (
        <>
          {renderTastingNotes()}
          {renderAllTastebudObjects()}
        </>
      )}
    </TastebudProfileContainer>
  );

  return isLoading ? <StyledLoading /> : renderContent();
};

export default TastebudProfileListPlotV2;
